import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { MapContainer, TileLayer, Marker, Popup, Polyline, CircleMarker, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { Truck, AlertTriangle, MapPin, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';

// Fix Leaflet's webpack-broken default marker icons (same as LocationPicker)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const GANSBAAI = [-34.5793, 19.3499];

const minsAgo = (t) => Math.round((Date.now() - new Date(t).getTime()) / 60000);
const freshness = (t) => {
  const m = minsAgo(t);
  if (m <= 10) return { label: `${m} min ago`, cls: 'text-green-600', dot: '#16a34a' };
  if (m <= 60) return { label: `${m} min ago`, cls: 'text-amber-600', dot: '#d97706' };
  const h = Math.floor(m / 60);
  return { label: `${h}h ${m % 60}m ago`, cls: 'text-gray-400', dot: '#9ca3af' };
};

// Pan/zoom the map to fit whatever's currently shown
function FitBounds({ points }) {
  const map = useMap();
  React.useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) { map.setView(points[0], 15); return; }
    map.fitBounds(L.latLngBounds(points), { padding: [40, 40] });
  }, [points, map]);
  return null;
}

export default function DriverMap() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState(null);   // driver_id whose trail is shown
  const [trailDate, setTrailDate] = useState(new Date().toISOString().split('T')[0]);

  const { data: latest = [], isFetching, refetch } = useQuery({
    queryKey: ['driver-latest'],
    queryFn: () => api.get('/drivers/locations/latest').then((r) => r.data),
    refetchInterval: 30000,
  });

  const { data: sos = [] } = useQuery({
    queryKey: ['sos-open'],
    queryFn: () => api.get('/drivers/sos').then((r) => r.data),
    refetchInterval: 30000,
  });

  const { data: trail } = useQuery({
    queryKey: ['driver-trail', selected, trailDate],
    queryFn: () => api.get(`/drivers/${selected}/trail`, { params: { date: trailDate } }).then((r) => r.data),
    enabled: !!selected,
  });

  // Route summary — distance/hours per driver over a period, from the trail
  const weekAgo = new Date(Date.now() - 6 * 86400000).toISOString().split('T')[0];
  const today = new Date().toISOString().split('T')[0];
  const [sumFrom, setSumFrom] = useState(weekAgo);
  const [sumTo, setSumTo] = useState(today);
  const { data: summary } = useQuery({
    queryKey: ['route-summary', sumFrom, sumTo],
    queryFn: () => api.get('/drivers/route-summary', { params: { from: sumFrom, to: sumTo } }).then((r) => r.data),
    enabled: sumFrom <= sumTo,
  });

  const markerPts = useMemo(
    () => latest.map((d) => [parseFloat(d.latitude), parseFloat(d.longitude)]),
    [latest]
  );
  const trailPts = useMemo(
    () => (trail?.points || []).map((p) => [parseFloat(p.latitude), parseFloat(p.longitude)]),
    [trail]
  );
  const fitPts = trailPts.length ? trailPts : markerPts;

  const resolveSos = async (id) => {
    try {
      await api.post(`/drivers/sos/${id}/resolve`, {});
      toast.success('SOS marked resolved');
      qc.invalidateQueries({ queryKey: ['sos-open'] });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not resolve');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Truck size={22} className="text-orange-500" />
          <div>
            <h1 className="text-xl font-bold text-gray-800">Driver Map</h1>
            <p className="text-xs text-gray-400">Where the vans are now, and where they&apos;ve been</p>
          </div>
        </div>
        <button onClick={() => refetch()} disabled={isFetching}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-orange-500">
          <RefreshCw size={15} className={isFetching ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {/* SOS — the thing you never want to miss */}
      {sos.length > 0 && (
        <div className="bg-red-600 text-white rounded-xl p-4 space-y-2">
          <p className="font-bold flex items-center gap-2"><AlertTriangle size={18} /> {sos.length} open SOS alert{sos.length === 1 ? '' : 's'}</p>
          {sos.map((a) => (
            <div key={a.id} className="flex flex-wrap items-center justify-between gap-2 bg-red-700/60 rounded-lg px-3 py-2">
              <span className="text-sm">
                <strong>{a.driver_name}</strong> · {new Date(a.created_at).toLocaleString('en-ZA', { dateStyle: 'short', timeStyle: 'short' })}
                {a.latitude != null && (
                  <> · <a className="underline" target="_blank" rel="noreferrer"
                    href={`https://www.google.com/maps?q=${a.latitude},${a.longitude}`}>open location</a></>
                )}
              </span>
              <button onClick={() => resolveSos(a.id)}
                className="text-xs bg-white text-red-700 font-semibold px-3 py-1 rounded-lg hover:bg-red-50">
                Mark resolved
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Driver status list */}
        <div className="bg-white rounded-xl shadow-sm p-3 lg:col-span-1">
          <h2 className="font-semibold text-gray-700 text-sm px-1 mb-2">Drivers reporting</h2>
          {latest.length === 0 ? (
            <p className="text-xs text-gray-400 px-1 py-6 text-center">
              No driver is reporting a location yet. The tracking app needs to be installed and a driver on shift.
            </p>
          ) : (
            <div className="space-y-1">
              {latest.map((d) => {
                const f = freshness(d.recorded_at);
                const on = selected === d.driver_id;
                return (
                  <button key={d.driver_id}
                    onClick={() => setSelected(on ? null : d.driver_id)}
                    className={`w-full text-left px-3 py-2 rounded-lg flex items-center gap-2 ${on ? 'bg-orange-50 ring-1 ring-orange-200' : 'hover:bg-gray-50'}`}>
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: f.dot }} />
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium text-gray-800 truncate">{d.driver_name}</span>
                      <span className={`block text-xs ${f.cls}`}>{f.label}</span>
                    </span>
                    <MapPin size={14} className="text-gray-300" />
                  </button>
                );
              })}
            </div>
          )}
          {selected && (
            <div className="mt-3 pt-3 border-t px-1">
              <label className="block text-xs font-medium text-gray-500 mb-1">Trail for</label>
              <input type="date" value={trailDate} onChange={(e) => setTrailDate(e.target.value)}
                className="w-full border rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-orange-400" />
              <p className="text-xs text-gray-400 mt-1">
                {trailPts.length ? `${trailPts.length} points` : 'No trail on this day.'}
              </p>
            </div>
          )}
        </div>

        {/* Map */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden lg:col-span-3" style={{ minHeight: 420 }}>
          <MapContainer center={GANSBAAI} zoom={9} style={{ height: 520, width: '100%' }}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FitBounds points={fitPts} />

            {/* Latest position per driver */}
            {latest.map((d) => (
              <Marker key={d.driver_id} position={[parseFloat(d.latitude), parseFloat(d.longitude)]}
                eventHandlers={{ click: () => setSelected(d.driver_id) }}>
                <Popup>
                  <strong>{d.driver_name}</strong><br />
                  {freshness(d.recorded_at).label}
                  {d.accuracy != null && <><br />±{Math.round(d.accuracy)}m</>}
                </Popup>
              </Marker>
            ))}

            {/* Selected driver's trail */}
            {trailPts.length > 1 && (
              <>
                <Polyline positions={trailPts} pathOptions={{ color: '#f97316', weight: 4, opacity: 0.8 }} />
                {trailPts.map((p, i) => (
                  <CircleMarker key={i} center={p} radius={3}
                    pathOptions={{ color: '#f97316', fillColor: '#f97316', fillOpacity: 1 }} />
                ))}
              </>
            )}
          </MapContainer>
        </div>
      </div>

      <p className="text-xs text-gray-400">
        Positions update every 30 seconds. A green dot means seen in the last 10 minutes, amber within the hour,
        grey older. Drivers record their route during the working hours set in Settings, with their consent.
      </p>

      {/* Route summary — how far each driver travelled */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <h2 className="font-semibold text-gray-700 text-sm flex items-center gap-2">
            <Truck size={16} className="text-orange-500" /> Route summary
          </h2>
          <div className="flex items-center gap-2 ml-auto text-xs">
            <input type="date" value={sumFrom} onChange={(e) => setSumFrom(e.target.value)}
              className="border rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-orange-400" />
            <span className="text-gray-400">to</span>
            <input type="date" value={sumTo} onChange={(e) => setSumTo(e.target.value)}
              className="border rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-orange-400" />
            {[['7d', 6], ['30d', 29]].map(([lbl, d]) => (
              <button key={lbl}
                onClick={() => { setSumFrom(new Date(Date.now() - d * 86400000).toISOString().split('T')[0]); setSumTo(today); }}
                className="px-2 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:border-orange-400">
                {lbl}
              </button>
            ))}
          </div>
        </div>
        {!summary?.drivers?.length ? (
          <p className="text-sm text-gray-400 text-center py-6">
            No route data in this period yet — drivers need the tracking app installed and to be on the road.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr>
                <th className="px-3 py-2 text-left">Driver</th>
                <th className="px-3 py-2 text-right">Distance</th>
                <th className="px-3 py-2 text-right">Days on road</th>
                <th className="px-3 py-2 text-right">Trail points</th>
                <th className="px-3 py-2 text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {summary.drivers.map((d) => (
                <tr key={d.driver_id} className="hover:bg-gray-50">
                  <td className="px-3 py-2.5 font-medium text-gray-800">{d.driver_name}</td>
                  <td className="px-3 py-2.5 text-right font-semibold text-gray-800">{d.km} km</td>
                  <td className="px-3 py-2.5 text-right text-gray-500">{d.days_active}</td>
                  <td className="px-3 py-2.5 text-right text-gray-400">{d.points}</td>
                  <td className="px-3 py-2.5 text-right">
                    <button onClick={() => { setSelected(d.driver_id); setTrailDate(sumTo); }}
                      className="text-xs text-orange-600 hover:underline">Show trail</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="text-xs text-gray-400 mt-2">
          Distance is measured along the route the phone recorded. Accuracy depends on how often it could get a fix.
        </p>
      </div>
    </div>
  );
}
